import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import { UserAvatarUploadButton } from '@/components/mine/user-avatar-upload-button';
import useUpdateNickName from '@/hooks/users/use-update-nickname';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES from '@/routes';
import { nicknameSchema } from '@/utils/schemas';

export default function NewcomerSetupRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<UpdateNicknameFormData>({
    resolver: zodResolver(updateNicknameFormSchema),
    defaultValues: { nickname: '' },
  });
  const { mutateAsync: updateNickname, isPending } = useUpdateNickName();

  const handleUpdateNickname = async (data: UpdateNicknameFormData) => {
    if (userInfo?.NickName === data.nickname) {
      navigate(ROUTES.MESSAGES.$path());
      return;
    }

    const response = await updateNickname(data.nickname);
    if (response.Code === 0) {
      navigate(ROUTES.MESSAGES.$path());
    }
  };

  return (
    <div className='flex flex-col flex-1 px-12 max-w-screen-xs w-full items-stretch h-full max-h-80vh 4xl:max-h-60vh gap-2 medium:gap-8'>
      <Link
        className='absolute desktop:static desktop:order-1 end-4 top-safe-offset-4 btn btn-ghost desktop:border text-primary-content'
        to={ROUTES.MESSAGES.$path()}>
        {t('auth.setup.do-later')}
      </Link>

      <div className='text-primary-content'>
        <h2 className='mb-8 text-center text-2xl font-bold'>{t('auth.setup.one-more-step')}</h2>

        <p className='text-body1 text-center'>{t('auth.setup.enter-info-to-finish')}</p>
      </div>

      <form
        className='flex flex-1 mt-4 medium:mt-12 justify-center gap-8 items-stretch flex-col'
        onSubmit={handleSubmit(handleUpdateNickname)}>
        <div className='flex flex-center'>
          <UserAvatarUploadButton showIndicator>
            <AvatarWithStatus
              className='size-30'
              size={[120, 120]}
              imageUrl={userInfo?.HeadImg}
            />
          </UserAvatarUploadButton>
        </div>

        <label
          htmlFor='nickname'
          className='form-control w-full'>
          <div className='label'>
            <span className='label-text text-neutral-0'>{t('personal-info.enter-nickname')}</span>
          </div>
          <FormInput
            control={control}
            name='nickname'
            disabled={isPending}
            maxLength={15}
            defaultValue=''
            helperText={t('fields.messages.max-length', { name: t('personal-info.nickname'), count: 15 })}
            helperTextClassName='text-primary-content'
            placeholder={userInfo?.NickName}
          />
        </label>

        <div className='flex-1' />

        <ProgressButton
          as='submit'
          isLoading={isPending}
          disabled={!isValid}
          className='btn btn-neutral text-primary disabled:btn-disabled w-full'
          label='auth.setup.join-now'
        />
      </form>

      <p className='text-primary-content text-body1 text-center'>{t('auth.setup.enter-info-later')}</p>
    </div>
  );
}

const updateNicknameFormSchema = z.object({
  nickname: nicknameSchema,
});

type UpdateNicknameFormData = z.infer<typeof updateNicknameFormSchema>;
